import type { Action } from '@reduxjs/toolkit'
import type { Observable } from 'rxjs'
import { catchError, debounceTime, defer, filter, firstValueFrom, from, groupBy, map, mergeMap, of, withLatestFrom } from 'rxjs'
import type { TRepositories } from '../../index.ts'
import type { State } from '../../Slice/index.ts'
import { orderWizardActions as wizard } from '../../Slice/OrderWizard/OrderWizardSlice.ts'
import type { SubmitChecks, SubmitReview } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import type { ValidationIssue } from '../../../Model/Order/ValidationIssue.ts'
import { Environment } from '../../../environment.ts'
import { testUser } from '../../../Util/stubs/testUser.stub.ts'
import type { WizardRepositories } from '../../../Services/OrderWizard/repository/index.ts'
import {
  describeSubmitIssues,
  dryRunSubmission,
  refusalOf,
  lengthCheckFor,
  overridePlatform,
  previewAfterUpdates,
  reviewSignature,
  submitWithChecks
} from '../../../Services/OrderWizard/submission.ts'
import { trackWizardSubmitted } from '../../../Services/OrderWizard/telemetry.ts'
import { currentStep, visibleSteps } from '../../../Services/OrderWizard/issues.ts'
import { submitChecksDue } from '../../../Services/OrderWizard/writeState.ts'
import { readsAtHand } from '../../../Services/OrderWizard/fileHandles.ts'
import type { DraftState } from '../../../Model/OrderWizard/OrderWizardDraft.ts'
import { draftIdOf } from './draftIdOf.ts'
import { draftOf } from './draftState.ts'
import type { WizardEpic } from './draftState.ts'

type RootState = typeof State

export type SubmitContext = {
  draftId: string
  state: () => Promise<RootState>
  repository: TRepositories['OrderRepository']
  submissions: WizardRepositories['OrderSubmissionRepository']
}

/** What Review & submit asks of the write queue; each runs after the writes before it, never during a pass. */
export const SUBMIT_REQUESTS = [wizard.reviewRequested, wizard.platformOverrideRequested, wizard.submitRequested] as const

export const isSubmitRequest = (action: Action): boolean => SUBMIT_REQUESTS.some((request) => request.match(action))

/* the same user the classic submit acts as, including the dev login bypass */
const submittingUser = (state: RootState) =>
  Environment.DEV_SKIP_LOGIN === 'true' && Environment.ENVIRONMENT !== 'production' ? testUser : state.authentication.user

const errorText = (error: unknown) => (error instanceof Error ? error.message : String(error))

const previewsOf = async (
  submissions: SubmitContext['submissions'],
  orderId: string
): Promise<Pick<SubmitReview, 'preview' | 'platform' | 'creditCost'>> => {
  const [preview, platform, creditCost] = await Promise.all([
    submissions.getSubmissionPreview(orderId).catch(() => undefined),
    submissions.getSequencingPlatformPreview(orderId).catch(() => undefined),
    submissions.getOrderCreditCost(orderId).catch(() => undefined)
  ])
  return { preview, platform, creditCost }
}

/**
 * One Review & submit request against the order as the server holds it once the queue got to it. The
 * draft, its signature and the user are read when the request runs, not when it was made, so findings
 * are filed against the draft they were made for. The review only reads; submit writes the primer's
 * length settings first, as the classic flow does.
 */
export const submitStep = (action: Action, { draftId, state, repository, submissions }: SubmitContext): Observable<Action> =>
  defer(async (): Promise<Action[]> => {
    const current = await state()
    const draft = current.orderWizard.drafts[draftId]
    const orderId = draft?.orderId
    if (!draft || !orderId || draft.submission) {
      return []
    }
    const user = submittingUser(current)
    const signature = reviewSignature(draft)
    const readLengths = draft.discovery?.readLengths
    const refused = (checks: Omit<SubmitChecks, 'signature'>) => wizard.submitRefused({ draftId, signature, checks })

    if (wizard.reviewRequested.match(action)) {
      try {
        const [order, pools, previews] = await Promise.all([
          firstValueFrom(repository.getOrder(orderId)),
          firstValueFrom(repository.getAllSamplePools({ orderId })),
          previewsOf(submissions, orderId)
        ])
        const readiness = await dryRunSubmission(order, pools, user, undefined, readLengths, draft.primerChoice)
        const platformIssues = previews.platform?.issues ?? []
        if (!readiness.ok) {
          return [
            wizard.reviewLoaded({ draftId, signature, refusal: refusalOf(readiness), platformIssues })
          ]
        }
        const review: SubmitReview = {
          order,
          pools,
          primer: readiness.primer,
          lengthCheck: lengthCheckFor(order, pools, readiness, readLengths),
          ...previews,
          preview: previewAfterUpdates(previews.preview, pools, readiness.updates)
        }
        return [wizard.reviewLoaded({ draftId, signature, review, platformIssues })]
      } catch (error) {
        return [wizard.reviewFailed({ draftId, signature, message: `The order could not be checked for submission: ${errorText(error)}` })]
      }
    }

    if (wizard.platformOverrideRequested.match(action)) {
      try {
        const order = await overridePlatform(orderId, action.payload.platform, repository)
        return [wizard.platformOverridden({ draftId, order, previews: await previewsOf(submissions, orderId) })]
      } catch (error) {
        return [wizard.platformOverrideFailed({ draftId, error: `The platform could not be changed: ${errorText(error)}` })]
      }
    }

    if (wizard.submitRequested.match(action)) {
      try {
        const readiness = await submitWithChecks(orderId, user, repository, undefined, readLengths, draft.primerChoice)
        if (!readiness.ok) {
          return [refused({ refusal: refusalOf(readiness) })]
        }
        trackWizardSubmitted()
        return [wizard.orderSubmitted({ draftId, at: new Date().toISOString() })]
      } catch (error) {
        const body = (error as { response?: unknown } | null)?.response
        return [
          refused(Array.isArray(body) ? { submitIssues: body as ValidationIssue[] } : { submitMessages: describeSubmitIssues(error) })
        ]
      }
    }
    return []
  }).pipe(
    mergeMap((actions) => from(actions)),
    catchError((error: unknown) => {
      console.error('Order wizard: a review or submit request failed', error)
      return of(failureOf(action, draftId, errorText(error)))
    })
  )

/* what leaves the page waiting on a request that failed outside its own handling */
const failureOf = (action: Action, draftId: string, message: string): Action =>
  wizard.platformOverrideRequested.match(action)
    ? wizard.platformOverrideFailed({ draftId, error: `The platform could not be changed: ${message}` })
    : wizard.reviewFailed({ draftId, message: `The order could not be checked for submission: ${message}` })

export const REVIEW_CHECK_SETTLE_MS = 400

/** Whether the Review step owes the lab a check: the order shows what the draft does, and the last check is of another draft. */
export const reviewCheckDue = (draft: DraftState | undefined): boolean => {
  if (!draft?.orderId || draft.submission || currentStep(draft, visibleSteps(draft)) !== 'review') {
    return false
  }
  const flow = draft.submitFlow
  return (
    flow?.stage !== 'checking' &&
    flow?.stage !== 'submitting' &&
    flow?.signature !== reviewSignature(draft) &&
    submitChecksDue(draft, (group) => readsAtHand(draft.id, group))
  )
}

/**
 * Review & submit checks the order on its own while the lab is on the Review step: on arriving, and once
 * the draft settles after a change. A check under way is followed by another once it lands, if due.
 */
export const reviewCheckEpic: WizardEpic = (action$, state$) =>
  action$.pipe(
    map(draftIdOf),
    filter((draftId): draftId is string => !!draftId),
    groupBy((draftId) => draftId),
    mergeMap((perDraft) =>
      perDraft.pipe(
        debounceTime(REVIEW_CHECK_SETTLE_MS),
        withLatestFrom(state$),
        filter(([draftId, state]) => reviewCheckDue(draftOf(state, draftId))),
        map(([draftId]) => wizard.reviewRequested({ draftId }))
      )
    )
  )
